// Shared time labels for streams of externally-timestamped things (news
// items, enquiries). Relative for the recent past people reason about
// relatively, absolute once "N days ago" stops being meaningful.

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function dayDifference(then: Date): number {
  return Math.round((startOfDay(new Date()) - startOfDay(then)) / 86_400_000);
}

// Grouping by day is what makes a mixed-source stream readable: without it, ten feeds
// interleave into an undifferentiated list. The label is relative for the two days
// people actually care about and absolute after that.
export function dayLabel(timestamp: string): string {
  const then = new Date(timestamp);
  const difference = dayDifference(then);

  if (difference === 0) return "Today";
  if (difference === 1) return "Yesterday";

  return then.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

export function agoLabel(minutes: number): string {
  if (minutes >= 60) {
    const hours = Math.round(minutes / 60);
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }
  return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
}

// A single "how long ago" phrase for one timestamp, e.g. next to an enquiry:
// "8 minutes ago", "2 hours ago", "Yesterday", "5 days ago", then a date.
export function sinceLabel(timestamp: string): string {
  const then = new Date(timestamp);
  const minutes = Math.round((Date.now() - then.getTime()) / 60_000);

  if (minutes < 1) return "just now";
  if (minutes < 24 * 60) return agoLabel(minutes);

  const difference = dayDifference(then);
  if (difference === 1) return "Yesterday";
  if (difference < 14) return `${difference} days ago`;

  // Only name the year when it is not this one: "5 August" a year on would
  // otherwise read as recent.
  if (then.getFullYear() !== new Date().getFullYear()) {
    return then.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
  return then.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
}

// Compact duration for stat cards: "45m", "6h", "3d". Buckets are chosen by
// the rounded value, not the raw seconds, so 59m30s reads "1h" rather than
// "60m".
export function durationShortLabel(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.round(seconds / 3600);
  if (hours < 48) return `${hours}h`;

  return `${Math.round(seconds / 86_400)}d`;
}

// When something is due, which is a different question from when something
// happened. sinceLabel answers the past and reads "just now" for anything
// ahead of the clock, so a follow-up planned for Tuesday says nothing at all.
//
// Late is said as late rather than as elapsed time: "3 days ago" describes when
// a thing happened, and the point of this row is that it has not.
export function dueLabel(timestamp: string): string {
  const when = new Date(timestamp);
  const difference = dayDifference(when);

  if (difference > 0) {
    return difference === 1 ? "1 day late" : `${difference} days late`;
  }

  if (difference === 0) return "today";
  if (difference === -1) return "tomorrow";

  // Near enough to count in days, far enough out to want the date. The
  // fortnight matches sinceLabel's, so the two read alike either side of now.
  if (difference > -14) return `in ${-difference} days`;

  return dateLabel(timestamp);
}

// A plain date, with the year only when it is not this one: "2 June" a year on
// would otherwise read as recent. Shared by everything that falls back to a
// date once a relative phrase stops meaning anything.
export function dateLabel(timestamp: string): string {
  const when = new Date(timestamp);

  if (when.getFullYear() !== new Date().getFullYear()) {
    return when.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  return when.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
}

// A month and a year, for facts that have no day worth showing: "March 2025".
// Design 8b renders an affiliation's start this way, because nobody knows the
// day somebody joined a board and printing one would be invented precision.
// Formatted in UTC because the value is a date with no time: the API sends
// "2025-03-01", which parses as UTC midnight, and rendering that in a
// negative-offset zone lands on the previous day and prints the month before.
export function monthLabel(date: string): string {
  return new Date(date).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}
